import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useValidationSchema } from './lib/useValidationSchema';
import { CreateLoginFields, Fields } from './models/fields';
import { Form, Input, Checkbox } from '~/shared/components/form';
import { Link, Radio } from '~/shared/components';
import { VisuallyHidden } from '~/shared/components/VisuallyHidden';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useFrame } from '~/shared/utils/frame';
import { useCityByPostCode } from '~/shared/hooks/useCitySearch/useCityByPostCode';
import { useRouter } from 'next/router';
import { notifyError, notifyInfo } from '~/shared/utils/errorBoundary/toast';
import { dateToString } from '~/utils/date-to-string';
import { Phone } from '~/shared/components/form/components/form-fields/phone';
import { theme } from '~/theme';
import Flex from '~/shared/components/Flex';
import {
    StyledInputTitle,
    StyledReadMore,
    StyledSpacer,
    StyledCTAButton,
    StyledInputWrapper,
    StyledLinkText,
    StyledInputText,
    StyledBirthDayText,
} from '../../styled';
import { TranslationKey } from '~/lib';
import { LoginDrawerMode } from '../../hooks/useLoginDrawerState';
import { useValidateEmailTld } from '~/features/checkout/hooks/useValidateEmailTld';

type CreateLoginProps = {
    translate: (key: TranslationKey) => string;
    setLoginDrawerMode: (mode: LoginDrawerMode) => void;
    closeAndReset: () => void;
    open: boolean;
    stayOnPageAfterSignup?: boolean;
};

export const CreateLoginView = ({
    translate,
    setLoginDrawerMode,
    closeAndReset,
    open,
    stayOnPageAfterSignup,
}: CreateLoginProps) => {
    const { data: frame } = useFrame();
    const countryCode = frame.culture?.split('-')[1].toUpperCase();
    const { validate } = useValidateEmailTld();
    const { replace } = useRouter();
    const validationSchema = useValidationSchema();
    const customer = useCustomer();
    const methods = useForm<CreateLoginFields>({
        mode: 'onChange',
        reValidateMode: 'onBlur',
        resolver: yupResolver(validationSchema),
    });
    const zipCode = methods.watch(Fields.PostalCode);
    const { cityName, hasNoMatchingPostCode, isFetchingCityByPostCode } =
        useCityByPostCode(zipCode);
    const isValid = methods.formState.isValid;

    const handleSubmit = async (payload: CreateLoginFields) => {
        validate(payload.email, {
            onSuccess: async (isValid) => {
                if (!isValid) {
                    notifyError(new Error(translate('checkout.errorTld')));
                    return;
                }

                const request = {
                    customer: {
                        firstName: payload.firstname,
                        lastName: payload.lastname,
                        email: payload.email ?? customer?.email,
                        birthday: dateToString(payload.birthday),
                        gender: Number(payload.gender),
                        address: payload.address,
                        postalCode: payload.postalCode,
                        city: payload.city,
                        phone: payload.mobile,
                        c_acceptedTermsAndConditions: payload.c_acceptedTermsAndConditions,
                        c_completeRegistration: true,
                        /**
                         * The signup terms says that the user automatically
                         * accepts marketing
                         */
                        c_newsletter: true,
                        c_socialMedia: true,
                    },
                    password: payload.password,
                };

                const address = {
                    addressId: 'main',
                    countryCode: countryCode!,
                    firstName: payload.firstname,
                    lastName: payload.lastname,
                    address1: payload.address,
                    address2: payload.address2,
                    postalCode: payload.postalCode,
                    city: payload.city,
                    phone: payload.mobile,
                };

                try {
                    await customer.registerCustomer({ ...request, addresses: [address] });
                } catch (e) {
                    if (
                        e instanceof Error &&
                        (e.message.includes('The login is already in use') ||
                            e.message.includes('The customer is already registered'))
                    ) {
                        notifyError(new Error(translate('createAccount.loginAlreadyInUse')));
                    } else {
                        notifyError(new Error(translate('message.generalErrorMessage')));
                    }
                    return;
                }

                notifyInfo(translate('createAccount.accountCreated'));

                const profileUrl = frame.settings?.pageReferences.myProfilePage?.url;
                setLoginDrawerMode(LoginDrawerMode.LOGIN);
                closeAndReset();

                if (stayOnPageAfterSignup) return;

                if (profileUrl) {
                    replace(profileUrl);
                } else {
                    replace('/');
                }
            },
        });
    };

    useEffect(() => {
        if (isFetchingCityByPostCode) {
            methods.setValue(Fields.City, '');
            return;
        }

        if (cityName) {
            methods.setValue(Fields.City, cityName);
            methods.clearErrors(Fields.PostalCode);
            return;
        }

        if (hasNoMatchingPostCode) {
            methods.setError(Fields.PostalCode, {
                message: translate('message.postNumberValidationError.notFound'),
            });
            methods.setValue(Fields.City, '');
            methods.setValue(Fields.PostalCode, '');
        }
    }, [cityName, hasNoMatchingPostCode, isFetchingCityByPostCode]);

    useEffect(() => {
        // The new member is logged in while the drawer is still open, so this would redirect too
        if (open && customer?.c_isMemberPlus && !stayOnPageAfterSignup) {
            replace(frame.settings?.pageReferences.myProfilePage?.url ?? '/');
        }
    }, [open, customer]);

    return (
        <Flex column style={{ marginTop: '40px' }}>
            <Flex column>
                <Form methods={methods} onSubmit={handleSubmit}>
                    <h4 style={{ textTransform: 'none' }}>
                        {translate('createAccount.becomeMemberOfImerco')}
                    </h4>
                    <div>
                        <StyledSpacer>
                            <StyledInputTitle>Fornavn & efternavn</StyledInputTitle>
                            <Input
                                data-testid="firstname"
                                name={Fields.FirstName}
                                placeholder={translate('createAccount.firstName')}
                                maxLength={24}
                                required
                            />
                            <Input
                                data-testid="lastname"
                                name={Fields.LastName}
                                placeholder={translate('createAccount.lastName')}
                                maxLength={24}
                                required
                            />
                        </StyledSpacer>
                        <StyledSpacer>
                            <StyledInputTitle>Personlige oplysninger *</StyledInputTitle>
                            <fieldset>
                                <VisuallyHidden asChild>
                                    <legend>Køn</legend>
                                </VisuallyHidden>
                                <Flex>
                                    <StyledInputWrapper>
                                        <Radio
                                            aria-label={translate('createAccount.female')}
                                            data-testid="gender-input"
                                            type="radio"
                                            name={Fields.Gender}
                                            value="2"
                                        />
                                        <StyledInputText>
                                            {translate('createAccount.female')}
                                        </StyledInputText>
                                    </StyledInputWrapper>
                                    <StyledInputWrapper>
                                        <Radio
                                            aria-label={translate('createAccount.male')}
                                            data-testid="gender-input"
                                            type="radio"
                                            name={Fields.Gender}
                                            value="1"
                                        />
                                        <StyledInputText>
                                            {translate('createAccount.male')}
                                        </StyledInputText>
                                    </StyledInputWrapper>
                                    <StyledInputWrapper>
                                        <Radio
                                            aria-label={translate('createAccount.genderOther')}
                                            data-testid="gender-input"
                                            type="radio"
                                            name={Fields.Gender}
                                            value="3"
                                        />
                                        <StyledInputText>
                                            {translate('createAccount.genderOther')}
                                        </StyledInputText>
                                    </StyledInputWrapper>
                                </Flex>
                            </fieldset>
                            <div>
                                <Input
                                    // Fixes Safari Browser styling issue
                                    style={{ alignContent: 'center' }}
                                    data-testid="birthday"
                                    type="date"
                                    name={Fields.Birthday}
                                    placeholder={translate('createAccount.birthday')}
                                />
                                <StyledBirthDayText>
                                    {translate('createAccount.weAskAboutYourBirthday')}
                                </StyledBirthDayText>
                            </div>
                        </StyledSpacer>
                        <StyledSpacer>
                            <StyledInputTitle>Kontaktoplysninger</StyledInputTitle>

                            <Input
                                data-testid="email"
                                name={Fields.Email}
                                placeholder={translate('createAccount.mail')}
                                maxLength={50}
                                required
                            />

                            <Input
                                data-testid="repeat-email"
                                name={Fields.RepeatEmail}
                                placeholder={translate('createAccount.repeatMail')}
                                maxLength={50}
                                required
                            />

                            <Phone
                                data-testid="phone-input"
                                name={Fields.Mobile}
                                placeholder={translate('createAccount.mobile')}
                                maxCharacters={8}
                                required
                            />

                            <Input
                                data-testid="address"
                                name={Fields.Address}
                                placeholder={translate('createAccount.address')}
                                maxLength={50}
                            />

                            <Input
                                data-testid="postalCode"
                                name={Fields.PostalCode}
                                placeholder={translate('createAccount.zip')}
                                maxLength={4}
                                required
                            />
                            <Input
                                data-testid="city"
                                name={Fields.City}
                                disabled
                                placeholder={translate('createAccount.city')}
                                required
                            />
                        </StyledSpacer>
                        <StyledSpacer>
                            <StyledInputTitle>Opret personlig kode</StyledInputTitle>
                            {/* TODO (IMC-2418):Forms: Input - Better handling of Help Text and Error Messages */}
                            <Input
                                data-testid="password"
                                name={Fields.Password}
                                type="password"
                                placeholder={translate('login.password')}
                                required
                                helpText={translate('message.passwordHelpText')}
                            />

                            <Input
                                data-testid="repeat-password"
                                name={Fields.RepeatPassword}
                                type="password"
                                placeholder={translate('createAccount.repeatPassword')}
                                required
                            />
                        </StyledSpacer>

                        <Checkbox
                            data-testid="accept-terms"
                            name={Fields.CreateAccountConsent}
                            label={translate('createAccount.acceptImercoPlusMembership')}
                            required
                        />
                        <StyledSpacer>
                            <Flex column>
                                <StyledReadMore>
                                    {translate('createAccount.readAboutImercos')}&nbsp;
                                    <Link
                                        aria-label={translate('createAccount.handlingOfPersonData')}
                                        color={theme.colors.black}
                                        noHover
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        title={translate('createAccount.handlingOfPersonData')}
                                        href={frame.market?.personalDataPolicyLink}
                                    >
                                        <StyledLinkText>
                                            {translate('createAccount.handlingOfPersonData')}
                                        </StyledLinkText>
                                    </Link>
                                </StyledReadMore>
                                <StyledReadMore>
                                    {translate('createAccount.readAbout')}&nbsp;
                                    <Link
                                        aria-label={translate(
                                            'createAccount.membershipTermsForImercoPlus',
                                        )}
                                        color={theme.colors.black}
                                        noHover
                                        rel="noopener noreferrer"
                                        target="_blank"
                                        title={translate(
                                            'createAccount.membershipTermsForImercoPlus',
                                        )}
                                        href={frame.market?.membershipTermsLink}
                                    >
                                        <StyledLinkText>
                                            {translate(
                                                'createAccount.membershipTermsForImercoPlus',
                                            )}
                                        </StyledLinkText>
                                    </Link>
                                </StyledReadMore>
                            </Flex>
                        </StyledSpacer>

                        <StyledCTAButton
                            data-testid="submit-registration"
                            type="submit"
                            variant="primary"
                            disabled={!isValid}
                        >
                            {translate('createAccount.becomeMember')}
                        </StyledCTAButton>
                    </div>
                </Form>
            </Flex>
        </Flex>
    );
};
